/**
 * <MobileMenu> — slide-down navigation panel shown when the burger is open
 * on small screens. Owns its own accordion state (Home subnav + Accounts
 * subnav); the parent <Layout> only needs to manage `mobileMenuOpen`.
 *
 * Inputs:
 *   - navigation:    top-level nav items (Home / Dashboard / etc) shared
 *                    with the desktop bar
 *   - homeSubLinks:  Pricing / Help / About links surfaced under Home
 *   - accounts:      current user's account list, surfaced under Accounts
 *   - user:          for the footer block (name / email / TierPill)
 *   - currentPath:   so we can highlight the active item
 *   - onClose:       called whenever a link/button is tapped, so the menu
 *                    auto-collapses after navigation
 *   - onLogout:      handler from Layout (wraps useAuth + navigate)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import TierPill from '../TierPill';
import { NAVBAR_BG, NAVBAR_BORDER, NAVBAR_MUTED } from './constants';

const MobileMenu = ({
  navigation,
  homeSubLinks,
  adminSubLinks = [],
  accounts = [],
  user,
  currentPath,
  onClose,
  onLogout,
}) => {
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [homeOpen, setHomeOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  return (
    <div
      className="md:hidden border-t"
      style={{ backgroundColor: NAVBAR_BG, borderTopColor: NAVBAR_BORDER }}
      data-testid="navbar-mobile-menu"
    >
      <div className="px-6 py-4 space-y-2">
        {navigation.map((item) => (
          <MobileNavItem
            key={item.name}
            item={item}
            currentPath={currentPath}
            homeSubLinks={homeSubLinks}
            adminSubLinks={adminSubLinks}
            accounts={accounts}
            homeOpen={homeOpen}
            setHomeOpen={setHomeOpen}
            accountsOpen={accountsOpen}
            setAccountsOpen={setAccountsOpen}
            adminOpen={adminOpen}
            setAdminOpen={setAdminOpen}
            onClose={onClose}
          />
        ))}
        <UserFooter user={user} onClose={onClose} onLogout={onLogout} />
      </div>
    </div>
  );
};

/**
 * Single-row renderer — branches three ways: Home (with subnav), Accounts
 * (with subnav listing user's accounts), or a plain Link.
 */
const MobileNavItem = ({
  item, currentPath, homeSubLinks, adminSubLinks, accounts,
  homeOpen, setHomeOpen, accountsOpen, setAccountsOpen, adminOpen, setAdminOpen, onClose,
}) => {
  const Icon = item.icon;
  const isAccounts = item.name === 'Accounts';
  const isHome = item.name === 'Home';
  const isAdmin = item.name === 'Admin';
  const homeSubActive = homeSubLinks.some((s) => currentPath === s.href);
  const adminSubActive = (adminSubLinks || []).some((s) => currentPath === s.href);
  const isActive = isAccounts
    ? (currentPath === '/accounts' || currentPath.startsWith('/accounts/'))
    : isHome
      ? (currentPath === item.href || homeSubActive)
      : isAdmin
        ? (currentPath.startsWith('/admin/') || adminSubActive)
        : currentPath === item.href;

  if (isHome) {
    return (
      <CollapsibleRow
        item={item}
        Icon={Icon}
        isActive={isActive}
        open={homeOpen}
        setOpen={setHomeOpen}
        toggleTestId="navbar-mobile-home-toggle"
        toggleAriaLabel="Toggle home menu"
        onClose={onClose}
      >
        {homeSubLinks.map((s) => (
          <SubLink
            key={s.href}
            to={s.href}
            label={s.label}
            onClose={onClose}
            testId={`navbar-mobile-home-item-${s.href.replace('/', '')}`}
          />
        ))}
      </CollapsibleRow>
    );
  }

  if (isAccounts && accounts.length > 0) {
    return (
      <CollapsibleRow
        item={item}
        Icon={Icon}
        isActive={isActive}
        open={accountsOpen}
        setOpen={setAccountsOpen}
        toggleTestId="navbar-mobile-accounts-toggle"
        toggleAriaLabel="Toggle accounts menu"
        onClose={onClose}
      >
        {accounts.map((acc) => (
          <SubLink
            key={acc.account_id}
            to={`/accounts/${acc.account_id}/statement`}
            label={acc.name}
            onClose={onClose}
            testId={`navbar-mobile-accounts-item-${acc.account_id}`}
            truncate
          />
        ))}
      </CollapsibleRow>
    );
  }

  if (isAdmin && (adminSubLinks || []).length > 0) {
    return (
      <CollapsibleRow
        item={item}
        Icon={Icon}
        isActive={isActive}
        open={adminOpen}
        setOpen={setAdminOpen}
        toggleTestId="navbar-mobile-admin-toggle"
        toggleAriaLabel="Toggle admin menu"
        onClose={onClose}
      >
        {adminSubLinks.map((s) => (
          <SubLink
            key={s.href}
            to={s.href}
            label={s.label}
            onClose={onClose}
            testId={`navbar-mobile-admin-item-${s.href.replace(/\//g, '-')}`}
          />
        ))}
      </CollapsibleRow>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onClose}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        isActive ? 'bg-[#0052FF] text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
      }`}
    >
      <Icon className="w-4 h-4" />
      {item.name}
    </Link>
  );
};

/**
 * Row with a primary <Link> + a chevron toggle for the subnav.
 */
const CollapsibleRow = ({
  item, Icon, isActive, open, setOpen, toggleTestId, toggleAriaLabel, onClose, children,
}) => (
  <div>
    <div className="flex items-center">
      <Link
        to={item.href}
        onClick={onClose}
        className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
          isActive ? 'bg-[#0052FF] text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
        }`}
      >
        <Icon className="w-4 h-4" />
        {item.name}
      </Link>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-2 p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10"
        aria-expanded={open}
        aria-label={toggleAriaLabel}
        data-testid={toggleTestId}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
    </div>
    {open && (
      <div className="mt-1 ml-4 border-l border-white/10 pl-3 space-y-1">
        {children}
      </div>
    )}
  </div>
);

const SubLink = ({ to, label, onClose, testId, truncate }) => (
  <Link
    to={to}
    onClick={onClose}
    className={`block px-3 py-2 text-sm rounded-lg text-white/80 hover:text-white hover:bg-white/10 ${truncate ? 'truncate' : ''}`}
    data-testid={testId}
  >
    {label}
  </Link>
);

/**
 * Footer of the mobile menu: name + tier pill, email, change-password link,
 * logout button.
 */
const UserFooter = ({ user, onClose, onLogout }) => (
  <div className="pt-4 border-t border-white/10">
    <div className="flex items-center gap-2 px-4 mb-1">
      <div className="text-sm font-medium text-white">{user?.name}</div>
      <TierPill user={user} />
    </div>
    <div className="text-xs px-4 mb-3" style={{ color: NAVBAR_MUTED }}>{user?.email}</div>
    <Link
      to="/account/settings"
      onClick={onClose}
      className="block px-4 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg mb-2"
      data-testid="navbar-mobile-account-settings-link"
    >
      Account settings
    </Link>
    <Button
      onClick={onLogout}
      className="w-full bg-white/10 text-white hover:bg-white/20 border border-white/10"
    >
      <LogOut className="w-4 h-4 mr-2" />
      Logout
    </Button>
  </div>
);

export default MobileMenu;
